import React, { useEffect, useRef, useState } from 'react';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../components/bootstrap/Card';
import { useFormik } from 'formik';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Input from '../../components/bootstrap/forms/Input';
import { OfferInterface, Role } from '../Role_Layout/roleTypes';
import Button from '../../components/bootstrap/Button';
import { emailErrors, fullName, joiningDateError } from '../UserManagement/UserValidation';
import { toast } from 'react-toastify';
import Label from '../../components/bootstrap/forms/Label';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import proLogo from '../../assets/logo.svg';
import { centralApi } from '../AxiosInstance/CreateInstance';
import ReactQuill from 'react-quill';
import { useLocation } from 'react-router-dom';


const formatToDDMMYYYY = (dateStr: string) => {
	if (!dateStr) return '';
	return dayjs(dateStr, 'YYYY-MM-DD').format('DD-MM-YYYY');
};

const formatToYYYYMMDD = (dateStr: string) => {
	if (!dateStr) return '';
	return dayjs(dateStr, 'DD-MM-YYYY').format('YYYY-MM-DD');
};

const today = new Date();
today.setDate(today.getDate() + 1);
const day = String(today.getDate()).padStart(2, '0');
const month = String(today.getMonth() + 1).padStart(2, '0');
const year = today.getFullYear();
const formattedDate = dayjs().format('DD-MM-YYYY');
const minDate = `${year}-${month}-${day}`;

interface IOfferProp {
	isFluid?: boolean;
}

interface OfferLetterSettings {
	_id: string;
	hrName: string;
	hrEmail: string;
	hrRole: string;
	hrSign: string;
	companyEmail: string;
	mobNoPrimary: string;
	mobNoSecondary: string;
	address: string;
	updatedBy: string;
	updatedAt: string;
	__v: number;
}

interface OfferFormValues {
	fName: string;


	email: string;
	Designation: string;
	location: string;
	salary: string;
	joiningDate: string;
}


const OfferGenerator: React.FC<IOfferProp> = ({ isFluid }) => {
	const [roles, setRoles] = useState<Role[]>([]);
	const bgColor = localStorage.getItem('facit_darkModeStatus');
	const navigate = useNavigate();
	const { offerId } = useParams();
	const [offers, setOffers] = useState<OfferInterface[]>([]);
	const [getOffer, setOffer] = useState<OfferInterface>();
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [htmlContent, setHtmlContent] = useState<string>('');
	const [offerLetterSettings, setOfferLetterSettings] =
		React.useState<OfferLetterSettings | null>(null);
	const isOfferLoaded = !!getOffer?.joiningDate;
	const [editableContent, setEditableContent] = useState('');
	const location = useLocation();
	const candidateData = location.state || {};



	useEffect(() => {
		const fetchOffers = async () => {
			try {
				const allOffers: any[] = [];
				let pageNo = 1;
				const limit = 50;
				let hasMore = true;

				while (hasMore) {
					// eslint-disable-next-line no-await-in-loop
					const res = await centralApi.get(
						`offerService/offer/list?pageNo=${pageNo}&limit=${limit}`,
					);
					const data = res.data?.data || [];
					allOffers.push(...data);
					if (data.length < limit) {
						hasMore = false;
					} else {
						pageNo += 1;
					}
				}
				setOffers(allOffers);
			} catch (error) {
				toast.error('Failed to fetch all offers');
			}
		};
		fetchOffers();
		const fetchRoles = async () => {
			await centralApi
				.get('userService/role/list')
				.then((res) => {
					setRoles(res.data.data);
				})
				.catch((err) => {
					toast.error('Failed to fetch Roles', { closeOnClick: true });
				});
		};
		fetchRoles();
		const fetchOfferLetterSettings = async () => {
			try {
				const res = await centralApi.get('offerService/offer/offerLetterSettings');
				setOfferLetterSettings(res.data.data);
			} catch (error) {
				toast.error('Failed to fetch offer letter settings');
			}
		};
		fetchOfferLetterSettings();
	}, []);

	useEffect(() => {
		if (offerId) {
			const findOffer = offers.find((item) => item._id === offerId);
			if (findOffer) {
				setOffer(findOffer);
			}
		}
	}, [offerId, offers]);

	const formik = useFormik<OfferFormValues>({
		enableReinitialize: true,
		initialValues: {

			fName: candidateData?.name || getOffer?.candidateId?.name || '',
			email: candidateData?.email || getOffer?.candidateId?.email || '',
			Designation: getOffer?.candidateId?.role || '',
			location: getOffer?.location || '',
			salary: getOffer?.salary || '12000',
			joiningDate: getOffer?.joiningDate ? formatToDDMMYYYY(getOffer.joiningDate) : '',
		},
		validate: (values) => {
			const errors: {
				fName?: string;
				email?: string;
				Designation?: string;
				salary?: string;
				joiningDate?: string;
				location?: string;
			} = {};

			if (fullName(values.fName)) {
				errors.fName = fullName(values.fName);
			}
			if (emailErrors(values?.email)) {
				errors.email = emailErrors(values?.email);
			}
			if (!values.Designation?.trim()) {
				errors.Designation = 'Please enter a designation';
			} else if (values.Designation.length > 50) {
				errors.Designation = 'Designation should not be more than 50 characters';
			} else if (values.Designation.length < 2) {
				errors.Designation = 'Designation must be at least 2 characters';
			} else if (!/^[A-Za-z0-9\s().#\/&_,-]+$/.test(values.Designation)) {
				errors.Designation = 'Designation can contain letters, numbers, spaces, and () . # / & - only';
			}
			if (!values.location || values.location.trim() === '') {
				errors.location = 'Location is required';
			} else if (values.location.length < 2) {
				errors.location = 'Location must be at least 2 characters';
			} else if (!/^[A-Za-z\s]+$/.test(values.location)) {
				errors.location = 'Location must contain only alphabetic characters and spaces';
			}
			if (values.location.length > 50) {
				errors.location = 'Please Enter location less than 50 characters';
			}
			if (joiningDateError(formatToYYYYMMDD(values.joiningDate))) {
				errors.joiningDate = joiningDateError(formatToYYYYMMDD(values.joiningDate));
			}
			if (!offerId && offers.find((item) => item.candidateId?.email === values?.email)) {
				errors.email = 'Email already exists';
			} else if (
				offerId &&
				offers.find(
					(item) =>
						item.candidateId?.email === values?.email &&
						item.candidateId?._id !== getOffer?.candidateId?._id,
				)
			) {
				errors.email = 'Email already exists';
			}
			return errors;
		},
		validateOnChange: true,
		validateOnBlur: true,
		onSubmit: async (values) => {
			if (Object.keys(formik.errors).length > 0) {
				toast.error('Please fill all the required fields', { closeOnClick: true });
				return;
			}

			if (offerId) {
				try {
					const candidateResponse = await centralApi.patch(
						'offerService/candidate/update',
						{
							candidateId: getOffer?.candidateId._id,
							role: values.Designation,
							name: values.fName,
							email: values.email,
						},
					);

					const offerResponse = await centralApi.patch('offerService/offer/update', {
						offerId: getOffer?._id,
						location: values.location,
						salay: values.salary,
						joiningDate: formatToYYYYMMDD(values.joiningDate),
					});

					toast.success('Candidate and Offer updated successfully', {
						closeOnClick: true,
					});
					navigate('/offer-list');
					formik.resetForm();
				} catch (error) {
					toast.error('Failed to update candidate or offer', { closeOnClick: true });
				}
			} else {
				try {
					await centralApi
						.post('offerService/offer/add', {
							candidateId: candidateData?.candidateId,   // ✅ Pass candidate ID
							jobRequisitionId: candidateData?.jobRequisitionId,
							name: values.fName,
							email: values.email,
							role: values.Designation,
							salary: values.salary,
							location: values.location,
							joiningDate: formatToYYYYMMDD(values.joiningDate),
							clauses: 'Standard probation of 3 months',
							offerContent: editableContent,
						})
						.then((res) => {
							toast.success('Candidate Added Successfully', { closeOnClick: true });
							navigate('/offer-list');
						});
				} catch (err) {
					toast.error('Error Occurred', { closeOnClick: true });
				}
			}
		},
	});

	useEffect(() => {
		const initialContent = `

      <div class="greeting">Dear <span id="candidate-name">${capitalizeWords(formik.values.fName)}</span>,</div>
      <div class="paragraph"><strong>Congratulations!!!</strong></div>
      <div class="paragraph">
        Further to our discussions, we are pleased to offer you a position with our organization as "<strong><span id="designation">${formik.values.Designation}</span></strong>."
        As mutually agreed, your date of joining would be <span id="joining-date">${formik.values.joiningDate}</span>.
      </div>
      <div class="paragraph"><strong>Job Location:</strong> <span id="location">${capitalizeWords(formik.values.location)}</span></div>
      <div class="paragraph">
        Please share a scan copy of the mentioned documents on Date of submission, and take a photocopy at the
        time of joining for further procedure:
      </div>
      <ol class="list">
        <li>2 Recent Passport Size Color Photographs</li>
        <li>Education Proof - original as well as photocopy</li>
        <li>Photocopy of Pan Card</li>
        <li>Photocopy of Aadhar card/Residence Proof</li>
        <li>Experience Letter of previous company</li>
        <li>Relieving Letter of previous company</li>
        <li>Salary slip/Bank statement of last 3 months</li>
        <li>One Blank cheque (at the time of joining)</li>
      </ol>
      <div class="paragraph"><strong>Note:</strong> Please bring original documents as well for the verification.</div>
      <div class="paragraph">
        In case any details pertaining to your reference checks or any documents submitted by you are found to be
        incorrect or misleading, the offer shall stand withdrawn.
      </div>
      <div class="paragraph"><strong>Terms:</strong></div>
      <ol class="list">
        <li>Your work timing will start from 09:30 a.m. for a nine hours shift.</li>
        <li>The Holidays and permissible leave will be as per the Rules.</li>
        <li>You will be on probation for 3 months.</li>
        <li>You have agreed to sign an agreement to work for a period of not less than 12 months.</li>
        <li>You have your own transport arrangement and you will not be provided with any such facility now or anytime in the future.</li>
      </ol>
      <div class="paragraph">
        Share a copy of 'Acceptance of your Resignation Letter from current organization' within 1-3 working days if applicable.
      </div>
      <div class="paragraph">
        Please sign the enclosed copy of the letter and return it to me by Date of submission to indicate your acceptance of this offer.
      </div>
      <div class="paragraph">
        We look forward to your association with us and wish you a rewarding career.
      </div>
     
    
`; setEditableContent(initialContent);
	}, [formik.values]);






	useEffect(() => {
		if (editableContent && offerLetterSettings && !htmlContent) {
			const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Offer Letter</title>
  <style type="text/css">
    body, p, h1, h2, h3, h4, h5, h6, ol, ul, li {
      margin: 0;
      padding: 0;
    }
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #000000;
      box-sizing: border-box;
    }
    .container {
      max-width: 700px;
      margin: 0 auto;
      border: 1px solid #e2e8f0;
    }
    .header {
      display: table;
      width: 100%;
      box-sizing: border-box;
      background-color: #fff9e6;
      padding: 15px;
    }
    .logo-container {
      display: table-cell;
      vertical-align: middle;
      width: 50%;
    }
    .contact-info {
      display: table-cell;
      vertical-align: middle;
      width: 50%;
      text-align: right;
    }
    .logo {
      background-color: #FFC107;
      color: #000000;
      font-size: 24px;
      font-weight: bold;
      padding: 15px;
      display: inline-block;
      border-radius: 4px;
    }
    .logo-text {
      font-weight: bold;
      margin-left: 8px;
    }
    .content {
      padding: 20px;
    }
    .title {
      text-align: center;
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 20px;
    }
    .date {
      text-align: right;
      margin-bottom: 15px;
    }
    .greeting {
      margin-bottom: 15px;
      font-weight: bold;
    }
    .paragraph {
      margin-bottom: 15px;
    }
    .list {
      margin-left: 25px;
      margin-bottom: 15px;
    }
    .list li {
      margin-bottom: 5px;
    }
	  ol, ul {
  margin-left: 25px;
  padding-left: 20px;
  margin-bottom: 15px;
}

ol li, ul li {
  margin-bottom: 5px;
  padding-left: 5px;
}

    .signature {
      margin-top: 30px;
    }
    .signature .name {
      font-style: italic;
      margin-bottom: 5px;
    }
    .acceptance {
      margin-top: 30px;
      padding-top: 15px;
      border-top: 1px solid #e2e8f0;
    }
    .welcome-text {
      text-align: center;
      font-weight: bold;
      margin: 15px 0;
    }
    .footer {
      background-color: #FFF0DB;
      padding: 10px;
      text-align: center;
      font-size: 12px;
    }
    a {
      color: #1A73E8;
      text-decoration: none;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="logo-container">
        <img src=${proLogo} alt='prometteur logo' />
        <div style="font-size: 12px;margin-left:4px">Ref ID: 1235876154165465</div>
      </div>
      <div class="contact-info">
        <div><a href="http://www.prometteursolutions.com">www.prometteursolutions.com</a></div>
        <div><a href="mailto:info@prometteursolutions.com">${offerLetterSettings?.companyEmail ?? 'Loading...'}</a></div>
        <div> ${offerLetterSettings?.mobNoPrimary ?? 'Loading...'} | ${offerLetterSettings?.mobNoSecondary ?? 'Loading...'}</div>
      </div>
    </div>
    <div class="content">
	 <div class="title">Offer Letter</div>
      <div class="date"><strong>Date:</strong> ${formattedDate}</div>
     <div id="editable-content-container">
  ${editableContent}
</div>

	    <div class="signature">
        <div>Sincerely,</div>
        <div class="name" style="margin-top: 15px;">
          ${offerLetterSettings?.hrSign
					? `<img src="${process.env.REACT_APP_CENTRAL_API_URL}offerService/${offerLetterSettings.hrSign}" alt="HR Signature" style="margin-top: 15px; max-height: 100px;" />`
					: 'Loading...'
				}
        </div>
        <div>${offerLetterSettings?.hrName ? capitalizeWords(offerLetterSettings.hrName) : 'Loading...'}</div>
        <div>${offerLetterSettings?.hrRole ? capitalizeWords(offerLetterSettings.hrRole) : 'Loading...'}</div>
        <div>Prometteur Solutions Pvt. Ltd.</div>
      </div>
      <div class="acceptance">
        <div>Accepted by,</div>
        <div style="margin-top: 25px;"><span id="acceptance-name">${capitalizeWords(formik.values.fName)}</span></div>
        <div><span id="acceptance-email">${formik.values.email}</span></div>
      </div>
    </div>
    <div class="footer">
      ${offerLetterSettings?.address ?? 'Loading...'}
    </div>
  </div>
</body>
</html>`;
			setHtmlContent(html)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editableContent, offerLetterSettings]);

	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe || !iframe.contentDocument) return;

		const doc = iframe.contentDocument;
		const editableContainer = doc.getElementById('editable-content-container');

		if (editableContainer) {
			editableContainer.innerHTML = editableContent;
		}
	}, [editableContent]);





	useEffect(() => {
		const iframe = iframeRef.current;
		if (iframe && iframe.contentDocument) {
			const doc = iframe.contentDocument;
			const updateElement = (id: string, value: string) => {
				const element = doc.getElementById(id);
				if (element) {
					element.textContent = value || '';
				}
			};

			updateElement('candidate-name', capitalizeWords(formik.values.fName));
			updateElement('designation', formik.values.Designation);
			updateElement('joining-date', formik.values.joiningDate);
			updateElement('location', capitalizeWords(formik.values.location));
			updateElement('acceptance-name', capitalizeWords(formik.values.fName));
			updateElement('acceptance-email', formik?.values?.email);
		}
	}, [
		formik.values.fName,
		formik.values.Designation,
		formik.values.joiningDate,
		formik.values.location,
		formik.values.email,
	]);

	function capitalizeWords(str: string) {
		return str
			.toLowerCase()
			.split(' ')
			.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	return (
		<PageWrapper className='mt-2'>
			<Card stretch='full' style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
				<CardHeader borderSize={1}>
					<CardLabel icon='TextSnippet' iconColor='info' className='ms-4'>
						<CardTitle>Manual Offer Letter Generator</CardTitle>
					</CardLabel>
				</CardHeader>
				<CardBody style={{ flex: 1, overflowY: 'auto' }}>
					<div className='row g-3' style={{ height: '100%' }}>
						<div className='col-lg-5 col-md-12 col-sm-12 col-12'>
							<Card className='d-flex flex-column h-100'>
								<CardHeader
									borderSize={1}
									className='d-flex justify-content-center'>
									<CardLabel
										icon='template'
										iconColor='info'
										className='text-center'>
										<CardTitle>Offer Details</CardTitle>
									</CardLabel>
								</CardHeader>
								<CardBody
									style={{ flex: '1 1 auto', overflowY: 'auto' }}
									isScrollable={isFluid}>
									<form onSubmit={formik.handleSubmit}>
										<div className='row mb-2'>
											<div className='col'>
												<FormGroup id='fName' label='Name' className='mb-3'>
													<Input
														type='text'
														id='fName'
														autoComplete='off'
														className='form-control py-2'
														placeholder='Enter Full Name'
														value={capitalizeWords(formik.values.fName)}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														isTouched={!!formik.touched.fName}
														invalidFeedback={formik.errors.fName}
														isValid={
															formik.isValid && !formik.errors.fName
														}
														name='fName'
													/>
												</FormGroup>
											</div>
										</div>
										<div className='row mb-4'>
											<div className='col'>
												<FormGroup
													id='email'
													label='Email'
													className='mb-3'>
													<Input
														type='text'
														id='email'
														autoComplete='off'
														className='form-control py-2'
														placeholder='Enter Your Email'
														value={formik.values?.email}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														isTouched={!!formik.touched.email}
														invalidFeedback={formik.errors.email}
														isValid={
															formik.isValid && !formik.errors.email
														}
														name='email'
													/>
												</FormGroup>
											</div>
										</div>
										<div className='row mb-4'>
											<div className='col'>
												<FormGroup
													id='location'
													label='Location'
													className='mb-3'>
													<Input
														type='text'
														id='location'
														autoComplete='off'
														className='form-control py-2'
														placeholder='Enter Location'
														value={capitalizeWords(
															formik.values.location,
														)}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														isTouched={!!formik.touched.location}
														invalidFeedback={formik.errors.location}
														isValid={
															formik.isValid &&
															!formik.errors.location
														}
														name='location'
													/>
												</FormGroup>
											</div>
										</div>
										<div className='row mb-4'>
											<div className='col'>
												<FormGroup
													id='Designation'
													label='Designation'
													className='mb-3'>
													<Input
														type='text'
														id='Designation'
														className='form-control py-2'
														placeholder='Enter Designation'
														value={formik.values.Designation}
														onChange={formik.handleChange}
														onBlur={formik.handleBlur}
														isTouched={!!formik.touched.Designation}
														invalidFeedback={formik.errors.Designation}
														isValid={
															formik.isValid &&
															!formik.errors.Designation
														}
														name='Designation'
													/>
												</FormGroup>
											</div>
										</div>
										<div className='row mb-4'>
											<div className='col-6'>
												<FormGroup
													id='joiningDate'
													label='Joining Date'
													className='mb-3'>
													<Input
														type='date'
														id='joiningDate'
														className='form-control py-2'
														placeholder='Enter Joining Date'
														value={formatToYYYYMMDD(
															formik.values.joiningDate,
														)}
														onChange={(
															e: React.ChangeEvent<HTMLInputElement>,
														) => {
															formik.setFieldValue(
																'joiningDate',
																formatToDDMMYYYY(e.target.value),
															);
														}}
														onBlur={formik.handleBlur}
														isTouched={!!formik.touched.joiningDate}
														invalidFeedback={formik.errors.joiningDate}
														isValid={
															formik.isValid &&
															!formik.errors.joiningDate
														}
														name='joiningDate'
														min={minDate}
													/>
												</FormGroup>
											</div>
										</div>
										<div className='row mb-4'>
											<div className='col-12'>
												<p className='text-muted bold'>Edit Offer Letter Content</p>
												<ReactQuill
													value={editableContent}
													onChange={(value) => setEditableContent(value)}
													modules={{
														toolbar: [
															[{ header: [1, 2, 3, false] }],
															['bold', 'italic', 'underline', 'strike'],
															[{ list: 'ordered' }, { list: 'bullet' }],
															['link', 'image'],
															['clean'],
														],
													}}
													theme='snow'
													style={{ height: '300px', marginBottom: '60px' }} // ✅ Extra bottom space
												/>
											</div>
										</div>


										<div className='row mt-4'>
											<div className='col d-flex justify-content-end gap-2'>
												<div className='col-3 col-md-6'>
													<Button
														color='dark'
														className='w-100'
														onClick={() => navigate('/offer-list')}
														onMouseEnter={(e) => {
															(
																e.target as HTMLButtonElement
															).style.transform = 'scale(1.02)';
														}}
														onMouseLeave={(e) => {
															(
																e.target as HTMLButtonElement
															).style.transform = 'scale(1)';
														}}>
														Cancel
													</Button>
												</div>
												<div className='col-3 col-md-6'>
													<Button
														className='w-100 rounded-pill bg-primary hrms-gradient-btn'
														type='submit'

														onMouseEnter={(e) => {
															(
																e.target as HTMLButtonElement
															).style.transform = 'scale(1.03)';
															(
																e.target as HTMLButtonElement
															).style.boxShadow =
																'0 6px 16px rgba(78,205,196,0.5)';
															(
																e.target as HTMLButtonElement
															).style.opacity = '0.9';
														}}
														onMouseLeave={(e) => {
															(
																e.target as HTMLButtonElement
															).style.transform = 'scale(1)';
															(
																e.target as HTMLButtonElement
															).style.boxShadow =
																'0 4px 12px rgba(78,205,196,0.3)';
															(
																e.target as HTMLButtonElement
															).style.opacity = '1';
														}}>
														Save
													</Button>
												</div>
											</div>
										</div>
									</form>
								</CardBody>
							</Card>
						</div>
						<div className='col-lg-7 col-md-12 col-sm-12 col-12'>
							<Card className='d-flex flex-column h-100'>
								<CardHeader
									borderSize={1}
									className='d-flex justify-content-center'>
									<CardLabel>
										<CardTitle>Generated Template Preview</CardTitle>
									</CardLabel>
								</CardHeader>
								<CardBody isScrollable={isFluid}>
									<Label htmlFor='text-editor'>Offer Letter Preview</Label>
									<div
										className='border rounded'
										style={{ flexGrow: 1, height: '100%' }}>
										{htmlContent && (
											<iframe
												ref={iframeRef}
												srcDoc={htmlContent}
												style={{
													width: '100%',
													height: '100%',
													border: 'none',
													background: '#fff',
												}}
												title="Offer Letter Preview"
											/>
										)}

									</div>
								</CardBody>
							</Card>
						</div>
					</div>
				</CardBody>
			</Card>
		</PageWrapper>
	);
};

OfferGenerator.defaultProps = {
	isFluid: false,
};

export default OfferGenerator;
